import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MeasurementEvent } from '../../../models/parsed-measurement.model';
import { SeekEvent } from '../../../measured-data-details.types';

@Component({
  selector: 'app-event-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="evt-section">
      <div class="evt-header">
        <div class="evt-header__left">
          <div class="evt-header__main">
            <mat-icon class="evt-header__icon">timeline</mat-icon>
            <span class="evt-header__title" i18n="@@details.eventTable.title">検出イベント</span>
            <span class="evt-header__count" i18n="@@details.eventTable.count">{{ events().length }} 件検出</span>
          </div>
          <span class="evt-header__hint" i18n="@@details.eventTable.hint">行クリックで該当箇所にシーク</span>
        </div>
      </div>

      <div class="evt-table">
        <div class="evt-table__header">
          <span class="evt-col--time" i18n="@@details.eventTable.header.time">時刻</span>
          <span class="evt-col--type" i18n="@@details.eventTable.header.category">分類</span>
          <span class="evt-col--detail" i18n="@@details.eventTable.header.detail">詳細</span>
          <span class="evt-col--action"></span>
        </div>

        <div class="evt-table__body">
          @for (event of events(); track $index) {
            <div class="evt-table__row" (click)="onEventSeek(event)">
              <span class="evt-col--time">{{ formatTime(event.startTime) }}</span>
              <span class="evt-col--type">
                <span class="badge" [class.badge--swallow]="event.type === 'swallow'" [class.badge--cough]="event.type === 'cough'" [class.badge--other]="event.type === 'other'">
                  {{ badgeLabel(event.type) }}
                </span>
              </span>
              <span class="evt-col--detail">
                {{ eventDetail(event, $index) }}
                <span class="dur-pill">
                  <mat-icon class="dur-pill__icon">timer</mat-icon>
                  <ng-container i18n="@@details.eventTable.duration">持続:</ng-container> {{ formatDuration(event.durationMs) }}
                </span>
              </span>
              <span class="evt-col--action">
                <button mat-icon-button class="seek-btn" [matTooltip]="seekTooltip" (click)="onEventSeek(event); $event.stopPropagation()">
                  <mat-icon>play_circle_outline</mat-icon>
                </button>
              </span>
            </div>
            @if (!$last) {
              <div class="evt-table__divider"></div>
            }
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .evt-section { padding: 0; }
    .evt-header {
      display: flex;
      align-items: center;
      padding: var(--space-sm) var(--space-md);

      @media (min-width: 768px) {
        padding: var(--space-sm) var(--space-lg);
      }
    }
    .evt-header__left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      width: 100%;

      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }
    .evt-header__main {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--space-xs);

      @media (min-width: 768px) {
        flex-wrap: nowrap;
        gap: var(--space-sm);
      }
    }
    .evt-header__icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      color: var(--gokuri-text-secondary);

      @media (min-width: 768px) {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
    .evt-header__title {
      font-size: var(--font-size-body-sm);
      font-weight: 600;
      color: var(--gokuri-text-primary);

      @media (min-width: 768px) {
        font-size: var(--font-size-body-md);
      }
    }
    .evt-header__hint {
      font-size: 10px;
      line-height: 1.2;
      color: var(--gokuri-text-tertiary);
      white-space: nowrap;

      @media (min-width: 768px) {
        font-size: 10px;
      }
    }
    .evt-header__count {
      font-size: 10px;
      font-weight: 500;
      color: #ffffff;
      background: var(--gokuri-primary);
      padding: 2px 8px;
      border-radius: var(--radius-full);
      white-space: nowrap;

      @media (min-width: 768px) {
        font-size: var(--font-size-label-sm);
        padding: var(--space-xs) 10px;
      }
    }
    .evt-table__header {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      padding: var(--space-sm) var(--space-md);
      background: var(--main-content-bg, #fafafa);
    }
    .evt-table__header span {
      font-size: var(--font-size-label-sm);
      font-weight: 600;
      color: var(--gokuri-text-secondary);
    }
    .evt-table__body {
      max-height: 185px;
      overflow-y: auto;
    }
    .evt-table__row {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      padding: 10px var(--space-md);
      cursor: pointer;
      transition: background-color var(--transition-fast, 150ms ease);
    }
    .evt-table__row:hover {
      background-color: var(--gokuri-surface-hover, rgba(0,0,0,0.04));
    }
    .evt-table__divider {
      height: 1px;
      background: var(--gokuri-border-light, #f0f0f0);
      margin: 0 var(--space-md);
    }
    .evt-col--time {
      width: 48px;
      font-size: 10px;
      color: var(--gokuri-text-primary);
      white-space: nowrap;

      @media (min-width: 768px) {
        width: 80px;
        font-size: var(--font-size-body-sm);
      }
    }
    .evt-col--type {
      width: 64px;
      flex-shrink: 0;

      @media (min-width: 768px) { width: 80px; }
    }
    .evt-col--detail {
      flex: 1;
      min-width: 0;
      font-size: var(--font-size-body-sm);
      color: var(--gokuri-text-primary);
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-2xs);

      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
        gap: var(--space-sm);
      }
    }
    .evt-col--action {
      width: 36px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (min-width: 768px) { width: 40px; }
    }
    .badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 500;
      padding: 2px 6px;
      border-radius: var(--radius-sm);
      white-space: nowrap;

      @media (min-width: 768px) {
        font-size: 11px;
        padding: 2px 8px;
      }
    }
    .badge--swallow { background: #00ccff15; color: #00ccff; }
    .badge--cough { background: #ff6b3515; color: #ff6b35; }
    .badge--other { background: #9c27b015; color: #9c27b0; }
    .dur-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      font-weight: 500;
      color: var(--gokuri-text-secondary);
      background: var(--gokuri-background, #f5f5f5);
      padding: 2px 8px;
      border-radius: 10px;
      white-space: nowrap;
    }
    .dur-pill__icon {
      font-size: 12px;
      width: 12px;
      height: 12px;
    }
    .seek-btn {
      width: 32px;
      height: 32px;
      color: var(--gokuri-primary);
    }
    /* モバイルでは小さな再生ボタンを廃し、行タップ全体で再生する。
       行は padding 10px で十分なタッチターゲットを持つ。 */
    @media (max-width: 767px) {
      .evt-col--action,
      .seek-btn {
        display: none;
      }
    }
  `],
})
export class EventTableComponent {
  readonly events = input.required<MeasurementEvent[]>();
  readonly seekToEvent = output<SeekEvent>();

  protected readonly seekTooltip = $localize`:@@details.eventTable.seekTooltip:該当箇所にシーク`;

  protected onEventSeek(event: MeasurementEvent): void {
    this.seekToEvent.emit({
      startTime: event.startTime,
      durationMs: event.durationMs,
    });
  }

  protected formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toFixed(1).padStart(4, '0')}`;
  }

  protected formatDuration(ms: number): string {
    return `${(ms / 1000).toFixed(2)}s`;
  }

  protected badgeLabel(type: string): string {
    if (type === 'swallow') return $localize`:@@details.eventTable.badge.swallow:嚥下`;
    if (type === 'cough') return $localize`:@@details.eventTable.badge.cough:せき・むせ`;
    return $localize`:@@details.eventTable.badge.other:嚥下以外`;
  }

  protected eventDetail(event: MeasurementEvent, index: number): string {
    if (event.type === 'swallow') {
      const swallowIndex = this.events()
        .slice(0, index + 1)
        .filter(e => e.type === 'swallow').length;
      return $localize`:@@details.eventTable.detail.swallow:嚥下検出（${swallowIndex}回目）`;
    }
    if (event.type === 'cough') return $localize`:@@details.eventTable.detail.cough:せき・むせ検出`;
    return $localize`:@@details.eventTable.detail.other:嚥下以外検出`;
  }
}
